import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  useTheme,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';

const STATS_URL = 'https://lyorauraproperties-api.onrender.com/api/v1/Leads/stats/daily';
const LEADS_URL = 'https://lyorauraproperties-api.onrender.com/api/v1/Leads';

// Format a Date object as YYYY-MM-DD (used by <input type="date" />)
const formatDateForInput = (date) => date.toISOString().split('T')[0];

// Default date range: the last 30 days up to today
const getDefaultDates = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return {
    from: formatDateForInput(from),
    to: formatDateForInput(to),
  };
};

export const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const defaultDates = getDefaultDates();

  // ================== Daily stats state ==================
  const [filterType, setFilterType] = useState('range');
  const [from, setFrom] = useState(defaultDates.from);
  const [to, setTo] = useState(defaultDates.to);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [week, setWeek] = useState('');

  const [data, setData] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ================== Leads table state ==================
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // ================== Auth / Logout ==================
  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    navigate('/lyorauraauthlogin', { replace: true });
  };

  const fetchStats = useCallback(async () => {
    setError('');
    setLoading(true);

    try {
      const params = {};

      if (filterType === 'range') {
        if (!from || !to) {
          setLoading(false);
          return;
        }
        params.from = from;
        params.to = to;
      } else if (filterType === 'month') {
        if (!month || !year) {
          setLoading(false);
          return;
        }
        params.month = month;
        params.year = year;
      } else if (filterType === 'week') {
        if (!week || !month || !year) {
          setLoading(false);
          return;
        }
        params.week = week;
        params.month = month;
        params.year = year;
      } else if (filterType === 'year') {
        if (!year) {
          setLoading(false);
          return;
        }
        params.year = year;
      }

      const res = await axios.get(STATS_URL, { params });

      setData(res.data.data || []);
      setTotalLeads(res.data.totalLeads || 0);

      if (!res.data.data || res.data.data.length === 0) {
        setError('No data available for this period');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(
        err.response?.data?.message ||
          err.message ||
          'An error occurred while fetching data - make sure the server is running'
      );
      setData([]);
      setTotalLeads(0);
    } finally {
      setLoading(false);
    }
  }, [filterType, from, to, month, year, week]);

  const fetchLeads = useCallback(async () => {
    setLeadsError('');
    setLeadsLoading(true);

    try {
      const params = { page, limit };

      if (debouncedSearchTerm) {
        params.keyword = debouncedSearchTerm;
      }

      const res = await axios.get(LEADS_URL, { params });

      setLeads(res.data.data || []);
      setTotalItems(res.data.pagination?.totalItems || 0);
      setTotalPages(res.data.pagination?.NumberOfPages || 1);
    } catch (err) {
      console.error('Error fetching leads:', err);
      setLeadsError(
        err.response?.data?.message ||
          err.message ||
          'An error occurred while fetching leads - make sure the server is running'
      );
      setLeads([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLeadsLoading(false);
    }
  }, [page, limit, debouncedSearchTerm]);

  // Load the last 30 days of stats on first render
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the search input (waits 400ms after the user stops typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load leads whenever the page or the debounced search term changes
  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();

    if (filterType === 'range' && from && to && new Date(from) > new Date(to)) {
      setError('The start date must be before the end date');
      return;
    }

    fetchStats();
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearFilter = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setPage(1);
  };

  const handlePageChange = (e, value) => {
    setPage(value);
  };

  const formatDateLabel = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* ================== Page header + Logout ================== */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Daily Lead Registration Statistics
        </Typography>

        <Button
          variant="outlined"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{ borderRadius: '10px', px: 2.5 }}
        >
Log Out
        </Button>
      </Box>

      {/* ================== Filters ================== */}
      <Paper
        component="form"
        onSubmit={handleSearch}
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: '14px',
          bgcolor: 'background.paper',
          border: '1px solid rgba(217,214,207,0.12)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 170, ...inputStyles }}>
          <InputLabel>Search type</InputLabel>
          <Select
            value={filterType}
            label="Search type"
            onChange={(e) => setFilterType(e.target.value)}
          >
            <MenuItem value="range">Date range</MenuItem>
            <MenuItem value="month">Specific month</MenuItem>
            <MenuItem value="week">Week in a month</MenuItem>
            <MenuItem value="year">Full year</MenuItem>
          </Select>
        </FormControl>

        {filterType === 'range' && (
          <>
            <TextField
              size="small"
              label="From date"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={inputStyles}
            />
            <TextField
              size="small"
              label="To date"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={inputStyles}
            />
          </>
        )}

        {(filterType === 'month' || filterType === 'week') && (
          <>
            <FormControl size="small" sx={{ minWidth: 110, ...inputStyles }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {i + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Year"
              type="number"
              placeholder="2026"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              sx={{ width: 110, ...inputStyles }}
            />
          </>
        )}

        {filterType === 'week' && (
          <FormControl size="small" sx={{ minWidth: 130, ...inputStyles }}>
            <InputLabel>Week number</InputLabel>
            <Select value={week} label="Week number" onChange={(e) => setWeek(e.target.value)}>
              <MenuItem value="1">Week 1</MenuItem>
              <MenuItem value="2">Week 2</MenuItem>
              <MenuItem value="3">Week 3</MenuItem>
              <MenuItem value="4">Week 4</MenuItem>
              <MenuItem value="5">Week 5</MenuItem>
            </Select>
          </FormControl>
        )}

        {filterType === 'year' && (
          <TextField
            size="small"
            label="Year"
            type="number"
            placeholder="2026"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            sx={{ width: 110, ...inputStyles }}
          />
        )}

        <Button type="submit" variant="contained" color="primary" sx={{ px: 3 }}>
          Search
        </Button>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          {error}
        </Alert>
      )}

      {/* ================== Total leads ================== */}
      <Chip
        label={`Total registrations: ${totalLeads}`}
        sx={{
          mb: 2,
          fontWeight: 700,
          fontSize: '14px',
          px: 1,
          bgcolor: 'primary.main',
          color: '#fff',
        }}
      />

      {/* ================== Chart ================== */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: '14px',
          bgcolor: 'background.paper',
          border: '1px solid rgba(217,214,207,0.12)',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: 'primary.main' }} />
          </Box>
        ) : data.length === 0 ? (
          <Typography sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            No data to display
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.7} />
                  <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(217,214,207,0.15)" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDateLabel}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              <Tooltip
                labelFormatter={(label) => `Date: ${label}`}
                formatter={(value) => [`${value} registrations`, 'Count']}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: '8px',
                  color: theme.palette.text.primary,
                }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                fill="url(#colorLeads)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* ================== Leads list ================== */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'text.primary' }}>
        Leads
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: '14px',
          bgcolor: 'background.paper',
          border: '1px solid rgba(217,214,207,0.12)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          label="Search by name, phone, email or country code"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ minWidth: 320, flex: 1, ...inputStyles }}
        />

        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClearFilter}
          disabled={!searchTerm}
          sx={{ px: 3 }}
        >
          Clear filter
        </Button>
      </Paper>

      {leadsError && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          {leadsError}
        </Alert>
      )}

      <Chip
        label={`Total leads: ${totalItems}`}
        sx={{
          mb: 2,
          fontWeight: 700,
          fontSize: '14px',
          px: 1,
          bgcolor: 'primary.main',
          color: '#fff',
        }}
      />

      <Paper
        elevation={0}
        sx={{
          borderRadius: '14px',
          bgcolor: 'background.paper',
          border: '1px solid rgba(217,214,207,0.12)',
          overflow: 'hidden',
        }}
      >
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Name', 'Email', 'Phone', 'Country Code', 'Message', 'Register Place', 'Date'].map(
                  (header) => (
                    <TableCell
                      key={header}
                      sx={{
                        bgcolor: 'background.paper',
                        color: 'text.secondary',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(217,214,207,0.15)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {header}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {leadsLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                    No leads found
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow key={lead._id} hover>
                    <TableCell sx={{ borderBottom: '1px solid rgba(217,214,207,0.08)' }}>
                      {lead.name || '-'}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(217,214,207,0.08)' }}>
                      {lead.email || '-'}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(217,214,207,0.08)' }}>
                      {lead.phone || '-'}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(217,214,207,0.08)' }}>
                      {lead.country_code || '-'}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid rgba(217,214,207,0.08)',
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={lead.message}
                    >
                      {lead.message || '-'}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(217,214,207,0.08)' }}>
                      {lead.registerPlace || '-'}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(217,214,207,0.08)', whiteSpace: 'nowrap' }}>
                      {formatDateTime(lead.created_date)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};